import { useCallback } from "react";
import type { DesignerEngine } from "@/core/engine/DesignerEngine";
import { buildHoldSolid } from "@/core/export/buildSolid";
import { meshToBinaryStl } from "@/core/export/stl";
import { useDesignerStore } from "@/state/designerStore";

export interface ExportSummary {
  triangles: number;
  bytes: number;
  filename: string;
}

/**
 * Build the final solid and hand the user an STL. Everything happens locally;
 * nothing is uploaded, and the file is produced from the same code path the
 * geometry tests exercise.
 */
export function useStlExport(engine: DesignerEngine | null): () => Promise<void> {
  return useCallback(async () => {
    if (!engine) return;
    const store = useDesignerStore.getState();
    store.setExportStatus({ state: "working", message: "Building solid…" });

    // Let the "working" state paint before we occupy the main thread.
    await new Promise((resolve) => setTimeout(resolve, 40));

    try {
      const built = await buildHoldSolid(
        engine.field,
        engine.fixings,
        store.exportQuality,
        engine.glossDabs,
      );
      if (built.report.triangleCount === 0) {
        store.setExportStatus({ state: "error", message: "Nothing left to export" });
        store.notify("There is no material left to export.", "warn");
        return;
      }
      // A hold that is not a closed solid will fail in a slicer, so refuse
      // rather than hand over a file that cannot be printed. Zero-area slivers
      // are harmless to slicers, so those only warn.
      if (!built.report.closed) {
        const detail = `${built.report.boundaryEdges} open, ${built.report.nonManifoldEdges} non-manifold edges`;
        store.setExportStatus({ state: "error", message: `Geometry check failed: ${detail}` });
        store.notify(
          "Export stopped because the solid is not watertight. Undo the last stroke and try again.",
          "warn",
        );
        return;
      }
      if (built.report.degenerateTriangles > 0) {
        store.notify(
          `${built.report.degenerateTriangles} zero-area triangle(s) in the mesh; slicers ignore these.`,
          "warn",
        );
      }

      const preset = engine.preset;
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      const filename = `ethos-hold-${preset.width}x${preset.depth}x${preset.height}-${stamp}.stl`;
      const { buffer, stats } = meshToBinaryStl(
        built.mesh,
        `ETHOS Hold Shaper V1.1 ${preset.width}x${preset.depth}x${preset.height}mm`,
      );

      downloadBuffer(buffer, filename);

      for (const warning of built.warnings) store.notify(warning, "warn");
      store.setExportStatus({
        state: "done",
        message: `${stats.triangleCount.toLocaleString()} triangles · ${formatBytes(stats.byteLength)}`,
      });
      store.notify(`Exported ${filename} — closed solid, flat rear face.`, "info");
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      store.setExportStatus({ state: "error", message });
      store.notify(`Export failed: ${message}`, "warn");
    }
  }, [engine]);
}

function downloadBuffer(buffer: ArrayBuffer, filename: string): void {
  const blob = new Blob([buffer], { type: "model/stl" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so Safari has time to start the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
